import React, { useState, useEffect, useRef } from "react";
import {
  FormControl,
  TextField,
  Button,
  Box,
  FormHelperText,
  Tooltip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { TONE_MOOD, URL } from "../utils/constants";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ThinBorder from "./ThinBorder";
import MultipleSignals from "./MultipleSignals";
import axios from "axios";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../authConfigFile/authConfig";
import { ReactComponent as UploadIcon } from "../assets/upload_icon_tone.svg";
import BackDrop from "./BackDrop";

import "./SideBarContainer.css";
import { isJwtTokenValid, refreshTokens } from "../utils/helper";

const SidebarTextfields = ({
  isSidebarOpen,
  handleSidebar,
  handleGenerateButtonClick,
  isButtonDisabled,
  formDataError,
  setFormDataError,
  formData,
  handleFormData,
  activeTab,
  setPersonaRequirementChanged,
  setPrintRequirementChanged,
  setEmailRequirementChanged,
  setSocialMediaRequirementChanged,
  setCreativeRequirementChanged,
  setFlyerRequirementChanged,
  tailoredVisible,
  signals,
  setSignals,
  selectedTitle,
  setSelectedTitle,
  setShowMediaLikeDragDrop,
  setGoogleAdsRequirementChanged,
  // Removed showHelperText and setShowHelperText from props
}) => {
  // State variables
  const [isToneLoading, setIsToneLoading] = useState(false);
  const toneSocketRef = useRef(null);
  const { instance } = useMsal();

  const getIdToken = async () => {
    try {
      if (localStorage.getItem("API_TOKEN")) {
        const token = localStorage.getItem("API_TOKEN");
        const isValid = isJwtTokenValid(token);
        if (isValid) {
          return `Bearer ${localStorage.getItem("API_TOKEN")}`;
        } else {
          await refreshTokens();
          return `Bearer ${localStorage.getItem("API_TOKEN")}`;
        }
      } else {
        const { idToken } = await instance.acquireTokenSilent(loginRequest);
        return `Bearer ${idToken}`;
      }
    } catch (error) {
      console.log(error);
    }
  };

  // Define showHelperText state locally within the component
  const [showHelperText, setShowHelperText] = useState({
    tone: false,
    product: false,
    objective: false,
    sellingPoint: false,
    // Add any other fields if necessary
  });

  const [fields, setFields] = useState({
    Persona: {
      Signals: true,
      Business: false,
      Story: false,
    },
    Print: {
      Signals: false,
      Business: true,
      Story: false,
    },
    Flyer: {
      Signals: false,
      Business: true,
      Story: false,
    },
    "Creative Concepts": {
      Signals: false,
      Business: true,
      Story: false,
    },
    Email: {
      Signals: false,
      Business: true,
      Story: false,
    },
    "Social Media": {
      Signals: false,
      Business: true,
      Story: false,
    },
    "Story Boarding": {
      Signals: false,
      Business: tailoredVisible,
      Story: true,
    },
  });

  const [signalDataChanged, setSignalDataChanged] = useState(false);
  const [businessDataChanged, setBusinessDataChanged] = useState(false);
  const [bottomLeftRadius, setBottomLeftRadius] = useState("0.75rem");
  const [bottomRightRadius, setBottomRightRadius] = useState("0.75rem");
  const [isSignalClicked, setIsSignalClicked] = useState(false);
  const [isBusinessClicked, setIsBusinessClicked] = useState(false);
  const [bottomLeftRadiusBusiness, setBottomLeftRadiusBusiness] =
    useState("0.75rem");
  const [bottomRightRadiusBusiness, setBottomRightRadiusBusiness] =
    useState("0.75rem");

  const openSignals = () => {
    setIsSignalClicked((prevClicked) => !prevClicked);
    if (!isSignalClicked) {
      setBottomLeftRadius("0px");
      setBottomRightRadius("0px");
    } else {
      setBottomLeftRadius("0.75rem");
      setBottomRightRadius("0.75rem");
    }
  };
  const handleChangeForSignals = () => {
    setFields({
      ...fields,
      [activeTab]: {
        ...fields[activeTab],
        Signals: !fields[activeTab]?.Signals,
      },
    });
  };
  const openBusiness = () => {
    setIsBusinessClicked((prevClicked) => !prevClicked);
    if (isBusinessClicked) {
      setBottomLeftRadiusBusiness("0px");
      setBottomRightRadiusBusiness("0px");
    } else {
      setBottomLeftRadiusBusiness("0.75rem");
      setBottomRightRadiusBusiness("0.75rem");
    }
  };
  const handleChangeForBusinessRequirements = () => {
    setFields({
      ...fields,
      [activeTab]: {
        ...fields[activeTab],
        Business: !fields[activeTab]?.Business,
      },
    });
  };

  const handleRequirementChanges = () => {
    if (signalDataChanged) {
      setPersonaRequirementChanged(true);
      setEmailRequirementChanged(true);
      setFlyerRequirementChanged(true);
      setSocialMediaRequirementChanged(true);
      setSignalDataChanged(false);
    }
    if (businessDataChanged) {
      setPrintRequirementChanged(true);
      setFlyerRequirementChanged(true);
      setEmailRequirementChanged(true);
      setSocialMediaRequirementChanged(true);
      setCreativeRequirementChanged(true);
      setGoogleAdsRequirementChanged(true);
      setBusinessDataChanged(false);
    }
  };

  // Handle file upload for Tone/Mood Desired
  const handleToneFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsToneLoading(true);
      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Please upload a .pdf or .docx file.");
        setIsToneLoading(false);
        return;
      }
      const formDataData = new FormData();
      formDataData.append("file", file);

      try {
        const idToken = await getIdToken();
        const response = await axios.post(TONE_MOOD, formDataData, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: idToken,
          },
        });

        const { uuid } = response.data;
        const token = encodeURIComponent(idToken.replace("Bearer ", ""));
        // Set up WebSocket connection
        const wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
        const wsUrl = `${wsProtocol}://qa-creativedge.deloitte.com/ws/text-processing/?uuid=${uuid}&token=${token}`;
        const socket = new WebSocket(wsUrl);
        toneSocketRef.current = socket;

        socket.onopen = () => {
          console.log("WebSocket connected");
        };

        socket.onmessage = (e) => {
          console.log("WebSocket message received:", e.data);
          const data = JSON.parse(e.data);
          if (data.tone_mood) {
            // Update the 'tone' field
            setBusinessDataChanged(true);
            handleFormData({ ...formData, tone: data.tone_mood });
            setShowHelperText((prev) => ({ ...prev, tone: true })); // Update helper text

            setIsToneLoading(false); // Hide loader
            socket.close(); // Close the socket
          } else if (data.error) {
            console.error("Error received:", data.error);
            setIsToneLoading(false); // Hide loader
            // Handle the error (e.g., show a notification)
            socket.close(); // Close the socket
          }
        };

        socket.onerror = (error) => {
          console.error("WebSocket error:", error);
          setIsToneLoading(false); // Hide loader
          socket.close();
        };

        socket.onclose = () => {
          console.log("WebSocket closed");
        };
      } catch (error) {
        console.error("Error uploading file:", error);
        setIsToneLoading(false); // Hide loader
        // Handle the error (e.g., show a notification)
      }
    }
  };

  // Clean up WebSocket on component unmount
  useEffect(() => {
    return () => {
      if (toneSocketRef.current) {
        toneSocketRef.current.close();
      }
    };
  }, []);

  return (
    <div className="flex flex-col items-center h-screen">
      {/* Render the loader when isToneLoading is true */}
      {isToneLoading && <BackDrop />}

      <div
        className={`  ${!isSidebarOpen && "scale-0"} ${
          (activeTab === "Story Boarding" && !tailoredVisible) ||
          ["Product Description", "Translate"].includes(activeTab)
            ? "opacity-50 pointer-events-none"
            : ""
        } flex flex-col justify-around gap-2`}
      >
        {/* Media Like Ad  */}

        <div className="flex mx-2 mt-4  justify-between items-center px-2 py-3  mb-2">
          <div
            className=" py-2 px-4  border border-[#4F4830] rounded-md flex justify-between items-center space-x-6 cursor-pointer"
            onClick={() => setShowMediaLikeDragDrop(true)}
          >
            <p className="font-semibold text-[0.9rem] text-[#4F4830]">
              LOOK-ALIKE AD
            </p>

            <UploadIcon className="w-5 h-5 text-[#4F4830]" />
          </div>

          <Tooltip
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "white",
                  color: "#000",
                  border: "0.5px solid gray",
                  maxWidth: "350px",
                  // fontWeight:"400",
                  fontFamily: "Open Sans",
                },
              },
            }}
            title={
              <p className="text-sm text-black font-normal font-sans">
                Have a great existing advertisement for your product/service?
                Simply upload the reference ad picture and the 'Look-alike Ad'
                function will automatically generate the audience signals and
                business requirements needed for your campaign.
              </p>
            }
            arrow
          >
            <InfoOutlinedIcon className="w-10 h-10  text-[#787878] hover:text-[#4F4830] cursor-pointer" />
          </Tooltip>
        </div>

        <ThinBorder alignment="horizontal" />

        <Accordion
          sx={{
            padding: "0.5rem 0",
            flexGrow: 1,
            overflowY: "auto",
            boxShadow: "none",
          }}
          expanded={fields[activeTab]?.["Signals"]}
          // expanded={fields[activeTab ==="Storyboard"] ? tailoredVisible :fields[activeTab]?.["Signals"] }
          onChange={() => {
            openSignals();
            handleChangeForSignals();
          }}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{
              backgroundColor: "#FFF",

              borderTopLeftRadius: "0.75rem",
              borderTopRightRadius: "0.75rem",
              borderBottomLeftRadius: isSignalClicked ? "0.75rem" : "0rem",
              borderBottomRightRadius: isSignalClicked ? "0.75rem" : "0rem",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>SIGNALS</Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: "#FFF",

              borderBottomLeftRadius: bottomLeftRadius,
              borderBottomRightRadius: bottomRightRadius,
            }}
          >
            <MultipleSignals
              signals={signals}
              setSignals={setSignals}
              formData={formData}
              formDataError={formDataError}
              handleFormData={handleFormData}
              setSignalDataChanged={setSignalDataChanged}
              showHelperText={showHelperText}
            />
          </AccordionDetails>
        </Accordion>

        <ThinBorder alignment="horizontal" />

        <Accordion
          sx={{
            padding: "0.5rem 0",
            flexGrow: 1,
            overflowY: "auto",
            boxShadow: "none",
            backgroundColor: "#fff",
          }}
          expanded={
            fields[activeTab]?.["Business"] ||
            (activeTab === "Story Boarding" && tailoredVisible)
          }
          onChange={() => {
            openBusiness();
            handleChangeForBusinessRequirements();
          }}
          disableGutters
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            sx={{
              backgroundColor: "#FFF",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              BUSINESS REQUIREMENTS
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{
              backgroundColor: "#FFF",
            }}
          >
            <FormControl sx={{ width: "100%" }}>
              <TextField
                label="Product / Service"
                multiline
                color="tertiary"
                rows={2}
                defaultValue=""
                fullWidth
                InputLabelProps={{
                  style: {
                    color: "#828282",
                  },
                }}
                sx={{
                  marginTop: 1,

                  "& .MuiFormLabel-root": {
                    fontWeight: 600,
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "currentColor",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    overflow: "hidden",
                  },
                  "& .MuiOutlinedInput-input": {
                    overflow: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  },
                }}
                focused
                placeholder="Enter product or service details"
                value={formData.product ? formData.product : ""}
                onChange={(e) => {
                  setBusinessDataChanged(true);
                  handleFormData({ ...formData, product: e.target.value });
                }}
                error={
                  (!formData.product.trim().length && formDataError.product) ||
                  (formData.product.trim().length &&
                    formDataError.product === "Remove the special characters.")
                }
                helperText={
                  (!formData.product.trim().length &&
                    formDataError.product === "This field is required.") ||
                  (formData.product.trim().length &&
                    formDataError.product === "Remove the special characters.")
                    ? formDataError.product
                    : ""
                }
              />
              {showHelperText["product"] && formData.product.length > 0 && (
                <FormHelperText sx={{ textAlign: "right" }}>
                  Extracted From An Uploaded Ad
                </FormHelperText>
              )}
            </FormControl>

            {/* Objective */}
            <FormControl sx={{ width: "100%" }}>
              <TextField
                label="Objective"
                color="tertiary"
                multiline
                rows={5}
                InputLabelProps={{
                  style: {
                    color: "#828282",
                  },
                }}
                fullWidth
                sx={{
                  marginTop: 3,
                  marginBottom: 3,
                  "& .MuiFormLabel-root": {
                    fontWeight: 600,
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "currentColor",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    overflow: "hidden",
                  },
                  "& .MuiOutlinedInput-input": {
                    overflow: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  },
                }}
                focused
                placeholder="Clearly define your objective, specifying the goal or purpose (Ex: Drive sales, increase following, increase views, promote a sale)"
                value={formData.objective}
                onChange={(e) => {
                  setBusinessDataChanged(true);
                  handleFormData({
                    ...formData,
                    objective: e.target.value,
                  });
                }}
                error={
                  (!formData.objective.trim().length &&
                    formDataError.objective) ||
                  (formData.objective.trim().length &&
                    formDataError.objective ===
                      "Remove the special characters.")
                }
                helperText={
                  (!formData.objective.trim().length &&
                    formDataError.objective === "This field is required.") ||
                  (formData.objective.trim().length &&
                    formDataError.objective ===
                      "Remove the special characters.")
                    ? formDataError.objective
                    : ""
                }
              />
              {showHelperText["objective"] && formData.objective.length > 0 && (
                <FormHelperText sx={{ textAlign: "right" }}>
                  Extracted From An Uploaded Ad
                </FormHelperText>
              )}
            </FormControl>

            {/* Tone/Mood Desired */}
            <FormControl sx={{ width: "100%", position: "relative" }}>
              <Box sx={{ position: "relative", marginTop: 3 }}>
                <TextField
                  label="Tone/Mood Desired"
                  multiline
                  color="tertiary"
                  rows={4}
                  InputLabelProps={{
                    style: {
                      color: "#828282",
                    },
                  }}
                  fullWidth
                  sx={{
                    "& .MuiFormLabel-root": {
                      fontWeight: 600,
                    },
                    "& .MuiInputBase-input.Mui-disabled": {
                      WebkitTextFillColor: "currentColor",
                      opacity: 1,
                    },
                    "& .MuiOutlinedInput-root": {
                      overflow: "hidden",
                    },
                    "& .MuiOutlinedInput-input": {
                      overflow: "auto",
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      "&::-webkit-scrollbar": {
                        display: "none",
                      },
                    },
                  }}
                  focused
                  placeholder="Describe the desired tone or mood for your campaign (ex: upbeat, luxurious, kid friendly, professional)"
                  value={formData.tone}
                  disabled={isToneLoading} // Disable while loading
                  onChange={(e) => {
                    setBusinessDataChanged(true);
                    handleFormData({ ...formData, tone: e.target.value });
                  }}
                  error={
                    (!formData.tone.trim().length && formDataError.tone) ||
                    (formData.tone.trim().length &&
                      formDataError.tone === "Remove the special characters.")
                  }
                  helperText={
                    (!formData.tone.trim().length &&
                      formDataError.tone === "This field is required.") ||
                    (formData.tone.trim().length &&
                      formDataError.tone === "Remove the special characters.")
                      ? formDataError.tone
                      : ""
                  }
                />

                {/* Upload Icon */}
                {/* <IconButton
                  color="primary"
                  aria-label="upload file"
                  component="label"
                  sx={{ position: "absolute", top: -25, right: -6 }}
                  disabled={isToneLoading}
                >
                  <input
                    hidden
                    type="file"
                    accept=".pdf,.docx"
                    onChange={handleToneFileChange}
                    multiple={false}
                  />
                  <UploadIcon />
                </IconButton> */}
              </Box>

              {showHelperText["tone"] && formData.tone.length > 0 && (
                <FormHelperText sx={{ textAlign: "right" }}>
                  Extracted From An Uploaded Ad
                </FormHelperText>
              )}
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <TextField
                label="Key Selling Points"
                multiline
                color="tertiary"
                rows={3}
                defaultValue=""
                InputLabelProps={{
                  style: {
                    color: "#828282",
                  },
                }}
                fullWidth
                sx={{
                  marginTop: 3,
                  marginBottom: 10,

                  "& .MuiFormLabel-root": {
                    fontWeight: 600,
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    WebkitTextFillColor: "currentColor",
                    opacity: 1,
                  },
                  "& .MuiOutlinedInput-root": {
                    overflow: "hidden",
                  },
                  "& .MuiOutlinedInput-input": {
                    overflow: "auto",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                  },
                }}
                focused
                placeholder="Highlight the key selling points that sets your product or service apart"
                value={formData.sellingPoint}
                onChange={(e) => {
                  setBusinessDataChanged(true);
                  handleFormData({
                    ...formData,
                    sellingPoint: e.target.value,
                  });
                }}
                error={
                  (!formData.sellingPoint.trim().length &&
                    formDataError.sellingPoint) ||
                  (formData.sellingPoint.trim().length &&
                    formDataError.sellingPoint ===
                      "Remove the special characters.")
                }
                helperText={
                  (!formData.sellingPoint.trim().length &&
                    formDataError.sellingPoint === "This field is required.") ||
                  (formData.sellingPoint.trim().length &&
                    formDataError.sellingPoint ===
                      "Remove the special characters.")
                    ? formDataError.sellingPoint
                    : ""
                }
              />
              {showHelperText["sellingPoint"] &&
                formData.sellingPoint.length > 0 && (
                  <FormHelperText sx={{ textAlign: "right" }}>
                    Extracted From An Uploaded Ad
                  </FormHelperText>
                )}
            </FormControl>
          </AccordionDetails>
        </Accordion>

        <ThinBorder alignment="horizontal" />

        <div className="p-6 w-full md:w-[21.2rem] mt-2  flex justify-center fixed bottom-0 bg-white z-10">
          <Button
            variant="contained"
            focused
            color="secondary"
            sx={{
              fontWeight: 600,
              width: { xs: "90%", md: "18.75rem" },
              backgroundColor: "#EFC634",
              "&:hover": {
                backgroundColor: "#FAF0D2",
              },
              "&.Mui-disabled": {
                opacity: 1,
                backgroundColor: "lightgray",
              },
            }}
            disabled={
              (activeTab === "Story Boarding" && !tailoredVisible) ||
              ["Product Description", "Translate"].includes(activeTab) ||
              isButtonDisabled
            }
            onClick={() => {
              handleRequirementChanges();
              handleGenerateButtonClick();
              setSelectedTitle(
                selectedTitle < signals.length ? selectedTitle : 0
              );
            }}
          >
            Generate
          </Button>
        </div>
      </div>

      {!isSidebarOpen && (
        <div className="h-full fixed bg-white">
          <p
            className="text-lg  rotate-180 px-1.5 py-2 "
            style={{ writingMode: "vertical-lr" }}
          >
            Signals and Business Requirements
          </p>
        </div>
      )}
    </div>
  );
};

export default SidebarTextfields;
